import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, BellIcon, PlaneIcon, UsersIcon } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Field, Toggle, inputClass } from '../../components/ui/Field';
import { eventTypeLabel, eventTitle, formatDate, functionLabel, functionOrder, fullName, glasbeniTipi, memberTypeLabel, memberTypeOrder, organizacijskiTipi, personalFieldLabel, personalFieldOrder, voiceLabel, voiceOrder } from '../../utils/labels';
import type { AudienceKind, ChoirEvent, ChoirFunction, EventType, MemberType, PersonalField, RoomMode, Voice } from '../../types';
const prazen = (privzetaLokacija: string): ChoirEvent => ({
  id: `e${Math.random().toString(36).slice(2, 7)}`,
  type: 'vaja',
  datum: '2026-12-04',
  zacetek: '19:00',
  konec: '21:00',
  lokacija: privzetaLokacija,
  steje: true,
  obcinstvo: 'aktivni'
});
const obsegOpcije: {
  key: AudienceKind;
  label: string;
}[] = [{
  key: 'aktivni',
  label: 'Vsi aktivni člani'
}, {
  key: 'vsi',
  label: 'Vsi člani in gostje'
}, {
  key: 'glasovi',
  label: 'Izbrane glasovne skupine'
}, {
  key: 'funkcije',
  label: 'Izbrane funkcije'
}, {
  key: 'izbrani',
  label: 'Ročno izbrani člani'
}, {
  key: 'sestavljeno',
  label: 'Sestavljen izbor (tipi, glasovi, funkcije, člani)'
}];
export function AdminEventForm() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    events,
    saveEvent,
    settings,
    expectedFor,
    addAnnouncement,
    pushToast,
    members
  } = useApp();
  const obstojec = events.find(e => e.id === id);
  const [osnutek, setOsnutek] = useState<ChoirEvent>(obstojec ?? prazen(settings.privzetaLokacija));
  const [vprasanje, setVprasanje] = useState(false);
  const pricakovani = useMemo(() => expectedFor(osnutek), [expectedFor, osnutek]);
  const poGlasovih = useMemo(() => {
    const map: Partial<Record<Voice, number>> = {};
    pricakovani.forEach(m => {
      map[m.voice] = (map[m.voice] ?? 0) + 1;
    });
    return map;
  }, [pricakovani]);
  const potovanje = osnutek.potovanje;
  const pomembnaSprememba = !!obstojec && (obstojec.datum !== osnutek.datum || obstojec.zacetek !== osnutek.zacetek || obstojec.lokacija !== osnutek.lokacija);
  function shrani() {
    saveEvent(osnutek);
    if (pomembnaSprememba) {
      setVprasanje(true);
      return;
    }
    pushToast(obstojec ? 'Dogodek je posodobljen.' : 'Nov dogodek je ustvarjen.');
    navigate(`/admin/dogodki/${osnutek.id}`);
  }
  function posljiObvestilo() {
    addAnnouncement({
      id: `a${Math.random().toString(36).slice(2, 7)}`,
      naslov: `Sprememba: ${eventTitle(osnutek)}`,
      besedilo: `Obveščamo vas, da je bil dogodek ${eventTitle(osnutek)} spremenjen.\n\nNov termin: ${formatDate(osnutek.datum, {
        weekday: true,
        long: true
      })} ob ${osnutek.zacetek}${osnutek.konec ? `–${osnutek.konec}` : ''}.\nLokacija: ${osnutek.lokacija}.\n\nProsimo, da si spremembo zabeležite in prihod potrdite v portalu.`,
      prioriteta: 'sprememba',
      avtor: 'Krištof Strnad',
      objavljeno: new Date().toISOString(),
      obcinstvoLabel: 'Udeleženci dogodka',
      prejemniki: pricakovani.map(m => m.id),
      zahtevaPotrditev: true,
      prebrali: []
    });
    setVprasanje(false);
    pushToast('Obvestilo o spremembi je bilo poslano.');
    navigate('/admin/obvestila');
  }
  function toggleIz<T>(seznam: T[] | undefined, vrednost: T): T[] {
    const trenutni = seznam ?? [];
    return trenutni.includes(vrednost) ? trenutni.filter(x => x !== vrednost) : [...trenutni, vrednost];
  }
  function nastaviPotovanje(patch: Partial<NonNullable<ChoirEvent['potovanje']>>) {
    setOsnutek({
      ...osnutek,
      potovanje: {
        aktivno: true,
        obveznaPolja: [],
        ...(osnutek.potovanje ?? {}),
        ...patch
      }
    });
  }
  const cipsKlasa = (izbran: boolean, barva: 'green' | 'purple' = 'green') => `rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${izbran ? barva === 'green' ? 'border-green-300 bg-green-50 text-green-700' : 'border-purple-300 bg-purple-50 text-purple-700' : 'border-line bg-white text-ink-muted hover:bg-canvas'}`;
  return <div className="mx-auto max-w-5xl space-y-4" data-id="element-905" data-mp="1loz00s89w3op">
      <button onClick={() => navigate(obstojec ? `/admin/dogodki/${obstojec.id}` : '/admin/dogodki')} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-906" data-mp="1zp01a8sg9d4p">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-907" data-mp="5dbks26kl2i7" />
        {obstojec ? 'Nazaj na dogodek' : 'Nazaj na dogodke'}
      </button>

      <div data-id="element-908" data-mp="2bzhiwuqjur5c">
        <h1 className="text-2xl font-semibold text-ink" data-id="element-909" data-mp="2byzg1f9dteog">{obstojec ? 'Uredi dogodek' : 'Nov dogodek'}</h1>
        <p className="mt-0.5 text-sm text-ink-muted" data-id="element-910" data-mp="2gz3xp8m10fg9">Določite tip, termin in za koga dogodek velja.</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]" data-id="element-911" data-mp="2364kxedmozce">
        <Card className="space-y-4 p-5" data-id="element-912" data-mp="278rf3lxruwcr">
          <div data-id="element-913" data-mp="277puapqyevhu">
            <p className="mb-2 text-[13px] font-medium text-ink" data-id="element-914" data-mp="27780s0voevdm">Tip dogodka</p>
            <div className="flex flex-wrap gap-2" data-id="element-915" data-mp="1t7yh2r89hj4w">
              {glasbeniTipi.map(t => <button key={t} type="button" onClick={() => setOsnutek({
              ...osnutek,
              type: t
            })} aria-pressed={osnutek.type === t} className={`rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${osnutek.type === t ? 'border-green-500 bg-green-500 text-white' : 'border-line bg-white text-ink-muted hover:bg-canvas hover:text-ink'}`} data-id="element-916" data-mp="1y94cwxx7xmin">
                  {eventTypeLabel[t]}
                </button>)}
            </div>
            <p className="mb-2 mt-3 text-[13px] font-medium text-ink" data-id="element-917" data-mp="mesjc7j5n9f4">Organizacijski dogodek</p>
            <div className="flex flex-wrap gap-2" data-id="element-918" data-mp="hfpikza7jpcy">
              {organizacijskiTipi.map((t: EventType) => <button key={t} type="button" onClick={() => setOsnutek({
              ...osnutek,
              type: t,
              steje: false,
              naslov: osnutek.naslov ?? (t === 'zbor' ? 'Redni zbor članov' : undefined)
            })} aria-pressed={osnutek.type === t} className={`rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${osnutek.type === t ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink-muted hover:bg-canvas hover:text-ink'}`} data-id="element-919" data-mp="1e5qb99dp6pzj">
                  {eventTypeLabel[t]}
                </button>)}
            </div>
          </div>

          <Field label="Naslov dogodka (neobvezno)" hint="Uporabno za nastope in zbore, npr. „Božični koncert“ ali „Redni zbor članov“." htmlFor="naslov" data-id="element-920" data-mp="2eeogz2nyy0jv">
            <input id="naslov" value={osnutek.naslov ?? ''} onChange={e => setOsnutek({
            ...osnutek,
            naslov: e.target.value || undefined
          })} placeholder={eventTypeLabel[osnutek.type]} className={inputClass} data-id="element-921" data-mp="2obkpv9hs4ooy" />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2" data-id="element-922" data-mp="324si93dopo7p">
            <Field label="Datum" htmlFor="datum" data-id="element-923" data-mp="321wlhidkp5ku">
              <input id="datum" type="date" value={osnutek.datum} onChange={e => setOsnutek({
              ...osnutek,
              datum: e.target.value
            })} className={inputClass} data-id="element-924" data-mp="373lwiy0spj8x" />
            </Field>
            <Field label="Zadnji dan (pri večdnevnih)" htmlFor="datumDo" data-id="element-925" data-mp="674088vmndln">
              <input id="datumDo" type="date" value={osnutek.datumDo ?? ''} onChange={e => setOsnutek({
              ...osnutek,
              datumDo: e.target.value || undefined
            })} className={inputClass} data-id="element-926" data-mp="17hok3vg65ey" />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2" data-id="element-927" data-mp="3jcue6wnovwdo">
            <Field label="Začetek" htmlFor="zacetek" data-id="element-928" data-mp="3jef7kiy9p99y">
              <input id="zacetek" type="time" value={osnutek.zacetek} onChange={e => setOsnutek({
              ...osnutek,
              zacetek: e.target.value
            })} className={inputClass} data-id="element-929" data-mp="3elufpmyx9692" />
            </Field>
            <Field label="Konec (neobvezno)" htmlFor="konec" data-id="element-930" data-mp="11fo637egakj1">
              <input id="konec" type="time" value={osnutek.konec ?? ''} onChange={e => setOsnutek({
              ...osnutek,
              konec: e.target.value || undefined
            })} className={inputClass} data-id="element-931" data-mp="11e36h3da4beb" />
            </Field>
          </div>

          <Field label="Lokacija" htmlFor="lokacija" data-id="element-932" data-mp="wguop3w1m9dl">
            <input id="lokacija" value={osnutek.lokacija} onChange={e => setOsnutek({
            ...osnutek,
            lokacija: e.target.value
          })} className={inputClass} data-id="element-933" data-mp="1k5w1f3bws6oo" />
          </Field>

          <Field label="Opis / navodila" htmlFor="opis" data-id="element-934" data-mp="1b57pce62sgef">
            <textarea id="opis" rows={3} value={osnutek.opis ?? ''} onChange={e => setOsnutek({
            ...osnutek,
            opis: e.target.value || undefined
          })} className={inputClass} data-id="element-935" data-mp="1f6b4eygau8el" />
          </Field>

          <Field label="Kratka opomba" hint="Prikaže se kot oznaka ob dogodku, npr. „v koncertnih oblekah“, „pol ure prej“." htmlFor="opomba" data-id="element-936" data-mp="2g6ahb1l01aeb">
            <input id="opomba" value={osnutek.opomba ?? ''} onChange={e => setOsnutek({
            ...osnutek,
            opomba: e.target.value || undefined
          })} placeholder="npr. Mozart Requiem" className={inputClass} data-id="element-937" data-mp="2l783dp94qol3" />
          </Field>
        </Card>

        <div className="space-y-4" data-id="element-938" data-mp="22ema9cplxc47">
          <Card className="space-y-4 p-5" data-id="element-939" data-mp="22foz7f3dd4hh">
            <Field label="Za koga velja" htmlFor="obcinstvo" data-id="element-940" data-mp="22cjba36h88nc">
              <select id="obcinstvo" value={osnutek.obcinstvo} onChange={e => setOsnutek({
              ...osnutek,
              obcinstvo: e.target.value as AudienceKind
            })} className={inputClass} data-id="element-941" data-mp="1ogp8m1waqfio">
                {obsegOpcije.map(o => <option key={o.key} value={o.key} data-id="element-942" data-mp="3mu7ehkmx87q1">
                    {o.label}
                  </option>)}
              </select>
            </Field>

            {osnutek.obcinstvo === 'sestavljeno' && <div data-id="element-943" data-mp="173phrnq2z6nf">
                <p className="mb-2 text-[13px] font-medium text-ink" data-id="element-944" data-mp="1kwx6n7ms1o1f">Tipi članstva</p>
                <div className="flex flex-wrap gap-2" data-id="element-945" data-mp="1kz1h68f6llao">
                  {memberTypeOrder.filter(t => t !== 'nekdanji').map((t: MemberType) => <button key={t} type="button" onClick={() => setOsnutek({
                ...osnutek,
                tipi: toggleIz(osnutek.tipi, t)
              })} aria-pressed={osnutek.tipi?.includes(t) ?? false} className={cipsKlasa(osnutek.tipi?.includes(t) ?? false)} data-id="element-946" data-mp="1ux7kmugmyzhn">
                        {memberTypeLabel[t]}
                      </button>)}
                </div>
              </div>}

            {(osnutek.obcinstvo === 'glasovi' || osnutek.obcinstvo === 'sestavljeno') && <div data-id="element-947" data-mp="29k1atieabbwj">
                <p className="mb-2 text-[13px] font-medium text-ink" data-id="element-948" data-mp="29jhx6dvo39ob">Glasovne skupine</p>
                <div className="flex flex-wrap gap-2" data-id="element-949" data-mp="2eiup3d1eb6xr">
                  {voiceOrder.filter(v => v !== 'brez').map(v => <button key={v} type="button" onClick={() => setOsnutek({
                ...osnutek,
                glasovi: toggleIz(osnutek.glasovi, v)
              })} aria-pressed={osnutek.glasovi?.includes(v) ?? false} className={cipsKlasa(osnutek.glasovi?.includes(v) ?? false, 'purple')} data-id="element-950" data-mp="2nk2ef5lxoxfp">
                        {voiceLabel[v]}
                      </button>)}
                </div>
              </div>}

            {(osnutek.obcinstvo === 'funkcije' || osnutek.obcinstvo === 'sestavljeno') && <div data-id="element-951" data-mp="zncnfj859zhy">
                <p className="mb-2 text-[13px] font-medium text-ink" data-id="element-952" data-mp="znutf7klzs32">Funkcije v zboru</p>
                <div className="flex flex-wrap gap-2" data-id="element-953" data-mp="umfapm5wlxnu">
                  {functionOrder.map((f: ChoirFunction) => <button key={f} type="button" onClick={() => setOsnutek({
                ...osnutek,
                funkcije: toggleIz(osnutek.funkcije, f)
              })} aria-pressed={osnutek.funkcije?.includes(f) ?? false} className={cipsKlasa(osnutek.funkcije?.includes(f) ?? false)} data-id="element-954" data-mp="qlvc0e5omlzl">
                      {functionLabel[f]}
                    </button>)}
                </div>
                <p className="mt-2 text-xs leading-relaxed text-ink-muted" data-id="element-955" data-mp="14l4fqecpbnmo">
                  Funkcija velja neodvisno od tipa članstva — vabljeni bodo tudi neaktivni, gostujoči in
                  častni člani s to funkcijo.
                </p>
              </div>}

            {(osnutek.obcinstvo === 'izbrani' || osnutek.obcinstvo === 'sestavljeno') && <div className="max-h-56 overflow-y-auto viva-scroll rounded-lg border border-line p-2" data-id="element-956" data-mp="1luouoqntsnee">
                {members.filter(m => m.type !== 'nekdanji').map(m => <label key={m.id} className="flex items-center gap-2 px-2 py-1.5 text-[13px] text-ink" data-id="element-957" data-mp="1cth8h6yl3d0o">
                      <input type="checkbox" className="h-4 w-4 rounded border-line accent-green-500" checked={osnutek.clani?.includes(m.id) ?? false} onChange={() => setOsnutek({
                ...osnutek,
                clani: toggleIz(osnutek.clani, m.id)
              })} data-id="element-958" data-mp="20jbk5l762vjc" />
                      {fullName(m)}
                      <span className="text-ink-muted" data-id="element-959" data-mp="2jftdg71nz3hp">· {memberTypeLabel[m.type]}</span>
                    </label>)}
              </div>}

            <Toggle checked={osnutek.steje} onChange={v => setOsnutek({
            ...osnutek,
            steje: v
          })} label="Šteje v prisotnost" opis="Dogodek se upošteva pri izračunu odstotka prisotnosti." data-id="element-960" data-mp="2xm4nzm2kjzqb" />
          </Card>

          <Card className="space-y-4 p-5" data-id="element-961" data-mp="32dwho5h0ulko">
            <div className="flex items-center gap-2" data-id="element-962" data-mp="32ext4b1y9a2x">
              <PlaneIcon className="h-4 w-4 text-purple-500" data-id="element-963" data-mp="2ense1y3u2lto" />
              <h2 className="text-base font-semibold text-ink" data-id="element-964" data-mp="2eobx5i57zg9a">Potovanje in logistika</h2>
            </div>
            <Toggle checked={!!potovanje?.aktivno} onChange={v => setOsnutek({
            ...osnutek,
            potovanje: v ? {
              ...(potovanje ?? {}),
              obveznaPolja: potovanje?.obveznaPolja ?? ['datumRojstva', 'dokumentStevilka'],
              aktivno: true
            } : undefined
          })} label="Potovalni podatki" opis="Vklopite samo pri gostovanjih, festivalih in večdnevnih dogodkih." data-id="element-965" data-mp="2aojnf243b42c" />

            {potovanje?.aktivno && <div className="space-y-4 border-t border-line pt-4" data-id="element-966" data-mp="fzlg8m182z35">
                <div className="grid gap-3 sm:grid-cols-2" data-id="element-967" data-mp="kxwq2wrip9ld">
                  <Field label="Odhod" htmlFor="odhod" data-id="element-968" data-mp="kvagysozam11">
                    <input id="odhod" value={potovanje.odhod ?? ''} onChange={e => nastaviPotovanje({
                  odhod: e.target.value
                })} placeholder="12. 3. 2027 ob 6.00" className={inputClass} data-id="element-969" data-mp="3jg1yzl359j2u" />
                  </Field>
                  <Field label="Vrnitev" htmlFor="prihod" data-id="element-970" data-mp="20c17por6b0r">
                    <input id="prihod" value={potovanje.prihod ?? ''} onChange={e => nastaviPotovanje({
                  prihod: e.target.value
                })} className={inputClass} data-id="element-971" data-mp="30npyz4zyqtvv" />
                  </Field>
                </div>
                <Field label="Zborno mesto" htmlFor="zborno" data-id="element-972" data-mp="2t5jeje57ynk3">
                  <input id="zborno" value={potovanje.zborno ?? ''} onChange={e => nastaviPotovanje({
                zborno: e.target.value
              })} className={inputClass} data-id="element-973" data-mp="2o5dscdhpxt9e" />
                </Field>
                <Field label="Navodila za potovanje" htmlFor="potOpis" data-id="element-974" data-mp="2aeik93f1ng6v">
                  <textarea id="potOpis" rows={2} value={potovanje.opis ?? ''} onChange={e => nastaviPotovanje({
                opis: e.target.value
              })} className={inputClass} data-id="element-975" data-mp="2acfjboccs2nd" />
                </Field>

                <div data-id="element-976" data-mp="1vjcemhjv1je2">
                  <p className="mb-2 text-[13px] font-medium text-ink" data-id="element-977" data-mp="1rfwhlk3vph0u">Za to pot potrebujemo</p>
                  <div className="flex flex-wrap gap-2" data-id="element-978" data-mp="3fclvewvy12ab">
                    {personalFieldOrder.map((p: PersonalField) => <button key={p} type="button" onClick={() => nastaviPotovanje({
                  obveznaPolja: toggleIz(potovanje.obveznaPolja, p)
                })} aria-pressed={potovanje.obveznaPolja.includes(p)} className={cipsKlasa(potovanje.obveznaPolja.includes(p), 'purple')} data-id="element-979" data-mp="3kbozae06t2gp">
                        {personalFieldLabel[p]}
                      </button>)}
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-ink-muted" data-id="element-980" data-mp="2rmi2pkkammy3">
                    Podatke črpamo iz profilov članov — obrazca ne izpolnjujejo znova. Organizator vidi le
                    popolnost podatkov.
                  </p>
                </div>

                <Toggle checked={!!potovanje.nastanitev?.aktivno} onChange={v => nastaviPotovanje({
              nastanitev: {
                aktivno: v,
                ime: potovanje.nastanitev?.ime,
                nacin: potovanje.nastanitev?.nacin ?? 'vodstvo',
                sobe: potovanje.nastanitev?.sobe ?? []
              }
            })} label="Nastanitev in sobe" opis="Sobe urejate v zavihku Nastanitev na dogodku." data-id="element-981" data-mp="2hqogf9grvf34" />

                {potovanje.nastanitev?.aktivno && <div className="grid gap-3 sm:grid-cols-2" data-id="element-982" data-mp="2qqxvu0uzs1sj">
                    <Field label="Naslov nastanitve" htmlFor="nastIme" data-id="element-983" data-mp="2mpm51s0dtair">
                      <input id="nastIme" value={potovanje.nastanitev.ime ?? ''} onChange={e => nastaviPotovanje({
                  nastanitev: {
                    ...potovanje.nastanitev!,
                    ime: e.target.value
                  }
                })} className={inputClass} data-id="element-984" data-mp="30pn8nf816w1x" />
                    </Field>
                    <Field label="Razporejanje sob" htmlFor="nastNacin" data-id="element-985" data-mp="3i6ybrrfff0c5">
                      <select id="nastNacin" value={potovanje.nastanitev.nacin} onChange={e => nastaviPotovanje({
                  nastanitev: {
                    ...potovanje.nastanitev!,
                    nacin: e.target.value as RoomMode
                  }
                })} className={inputClass} data-id="element-986" data-mp="3d9ewqpawmjj5">
                        <option value="vodstvo" data-id="element-987" data-mp="26qgqmnha4k4t">Razporeja vodstvo</option>
                        <option value="clani" data-id="element-988" data-mp="21sf4tusgy6pn">Člani izberejo sobo</option>
                      </select>
                    </Field>
                  </div>}

                <Toggle checked={!!potovanje.prevoz?.aktivno} onChange={v => nastaviPotovanje({
              prevoz: {
                aktivno: v
              }
            })} label="Uskladitev prevozov" opis="Člani se prijavijo kot voznik ali sopotnik." data-id="element-989" data-mp="m2ea1dsfx8o4" />
              </div>}
          </Card>

          <Card className="p-5" data-id="element-990" data-mp="1026z74objgtz">
            <div className="flex items-center gap-2" data-id="element-991" data-mp="150qhmv3peahl">
              <UsersIcon className="h-4 w-4 text-green-600" data-id="element-992" data-mp="1508dzdepotzn" />
              <h2 className="text-base font-semibold text-ink" data-id="element-993" data-mp="r1ighwnzcsar">Pričakovani udeleženci</h2>
            </div>
            <p className="mt-2 text-2xl font-semibold text-ink" data-id="element-994" data-mp="v1syk7p1r95z">{pricakovani.length}</p>
            <div className="mt-3 flex flex-wrap gap-1.5" data-id="element-995" data-mp="3bqonxmwnt83o">
              {voiceOrder.filter(v => poGlasovih[v]).map(v => <Badge key={v} className="bg-canvas text-ink-muted border-line" data-id="element-996" data-mp="3fqp1z6fujha7">
                    {voiceLabel[v]}: {poGlasovih[v]}
                  </Badge>)}
            </div>
            {pricakovani.length > 0 && pricakovani.length <= 12 && <p className="mt-3 text-[13px] leading-relaxed text-ink-muted" data-id="element-997" data-mp="1ciygqm30pqpz">
                {pricakovani.map(m => fullName(m)).join(', ')}
              </p>}
          </Card>

          <div className="flex gap-2" data-id="element-998" data-mp="13kmflq4izaja">
            <Button variant="secondary" full onClick={() => navigate(obstojec ? `/admin/dogodki/${obstojec.id}` : '/admin/dogodki')} data-id="element-999" data-mp="13hi6cec7dolr">
              Prekliči
            </Button>
            <Button full onClick={shrani} data-id="element-1000" data-mp="omu3l7y6t49u">
              {obstojec ? 'Shrani spremembe' : 'Ustvari dogodek'}
            </Button>
          </div>
        </div>
      </div>

      <Modal open={vprasanje} onClose={() => {
      setVprasanje(false);
      navigate(`/admin/dogodki/${osnutek.id}`);
    }} naslov="Dogodek je bil pomembno spremenjen" opis="Želite poslati obvestilo članom?" noga={<>
            <Button variant="secondary" onClick={() => {
        setVprasanje(false);
        pushToast('Dogodek je posodobljen brez obvestila.');
        navigate(`/admin/dogodki/${osnutek.id}`);
      }} data-id="element-1002" data-mp="2vgupy3uhmbhr">
              Shrani brez obvestila
            </Button>
            <Button onClick={posljiObvestilo} data-id="element-1003" data-mp="2cjthua5mzv9l">
              <BellIcon className="h-4 w-4" data-id="element-1004" data-mp="2ckle7n725x0x" />
              Pošlji obvestilo
            </Button>
          </>} data-id="element-1001" data-mp="2nx29xq1s7atz">
        <div className="space-y-3 text-sm text-ink" data-id="element-1005" data-mp="2hliwdzs2ir2f">
          <p data-id="element-1006" data-mp="2mjal6kxf1x2e">
            Spremenili ste termin ali lokacijo dogodka <strong data-id="element-1007" data-mp="3sybjuydtstco">{eventTitle(osnutek)}</strong>.
          </p>
          <div className="rounded-lg bg-canvas px-4 py-3 text-[13px] leading-relaxed text-ink-muted" data-id="element-1008" data-mp="3sxtjpqo1luzh">
            <p data-id="element-1009" data-mp="1q7738jcii6h">
              <span className="font-medium text-ink" data-id="element-1010" data-mp="1tv1e9wm78m9">Novo:</span>{' '}
              {formatDate(osnutek.datum, {
              weekday: true
            })} ob {osnutek.zacetek} · {osnutek.lokacija}
            </p>
            {obstojec && <p className="mt-1 line-through" data-id="element-1011" data-mp="1jpn7kusb3uee">
                {formatDate(obstojec.datum, {
              weekday: true
            })} ob {obstojec.zacetek} ·{' '}
                {obstojec.lokacija}
              </p>}
          </div>
          <p className="text-[13px] text-ink-muted" data-id="element-1012" data-mp="1tn1g887znew5">
            Obvestilo s prioriteto „Sprememba dogodka“ bo poslano {pricakovani.length} članom, z zahtevo po
            potrditvi branja.
          </p>
        </div>
      </Modal>
    </div>;
}